import { Table, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import TrashBin from '@components/icons/TrashBin.svg';
import { CustomConfirmationModal } from '@modals/common/confirmations/CustomConfirmationModal';
import { ModalFooterEnd } from '@modals/core-ui/ModalFooter/variations/ModalFooterEnd';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useAPI } from '@providers/useAPI';
import { PaymentMethodResponseDto, PaymentService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Content } from './styles/Content';

type PaymentMethodResponseDtoWithClassName = PaymentMethodResponseDto & {
  className?: string;
};

export const PaymentMethodsHistory = ({
  billingAccountId,
  onCancel,
}: {
  billingAccountId: string;
  onCancel: () => void;
}) => {
  const { call } = useAPI();
  const [isLoading, setIsLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodResponseDtoWithClassName[]>([]);
  const [deletePaymentMethod, setDeletePaymentMethod] = useState<PaymentMethodResponseDtoWithClassName>();
  const [hasLoadError, setHasLoadError] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setHasLoadError(false);
    try {
      const data = await call(() => PaymentService.billingAccountPaymentMethodList({ baId: billingAccountId }));
      const newMethods = data.map((item) => {
        if (item.default) {
          (item as PaymentMethodResponseDtoWithClassName).className = 'defaultMethod';
        }
        return item;
      });
      setPaymentMethods(newMethods);
    } catch (error: unknown) {
      setHasLoadError(true);
      if (error instanceof ApiCallError && error.status === 401) {
        return;
      }
      if (error instanceof ApiCallError && error.status === 403) {
        toast.error('You do not have access to payment methods');
        return;
      }
      toast.error('Unable to load payment methods. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleCancelPaymentMethod = async (paymentMethod: PaymentMethodResponseDtoWithClassName) => {
    setIsLoading(true);
    try {
      await call(() =>
        PaymentService.billingAccountCancelPaymentMethod({
          baId: billingAccountId,
          paymentMethodId: paymentMethod.id,
        }),
      );
      const newPaymentMethods: PaymentMethodResponseDtoWithClassName[] = await call(() =>
        PaymentService.billingAccountPaymentMethodList({ baId: billingAccountId }),
      );
      if (paymentMethod.default) {
        if (newPaymentMethods[0]) {
          await call(() =>
            PaymentService.billingAccountSetDefaultPaymentMethod({
              baId: billingAccountId,
              paymentMethodId: newPaymentMethods[0].id,
            }),
          );
          newPaymentMethods[0].default = true;
        }
      }
      const paymentMethodsWithClassName = newPaymentMethods.map((item) => {
        if (item.default) {
          item.className = 'defaultMethod';
        }
        return item;
      });
      setDeletePaymentMethod(undefined);
      setPaymentMethods(paymentMethodsWithClassName);
    } catch (error: unknown) {
      if (error instanceof ApiCallError && error.status === 401) {
        return;
      }
      toast.error('Unable to update payment methods. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    setIsLoading(true);
    try {
      if (!paymentMethods.some((method) => method.id === paymentMethodId)) {
        return;
      }
      await call(() =>
        PaymentService.billingAccountSetDefaultPaymentMethod({
          baId: billingAccountId,
          paymentMethodId,
        }),
      );
      setPaymentMethods(
        paymentMethods.map((method) => ({
          ...method,
          default: method.id === paymentMethodId,
          className: method.id === paymentMethodId ? 'defaultMethod' : '',
        })),
      );
    } catch (error: unknown) {
      if (error instanceof ApiCallError && error.status === 401) {
        return;
      }
      toast.error('Unable to update payment methods. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const deletePaymentMethodDescription = `Are you sure you want to delete ${
    deletePaymentMethod?.expDate?.month
  }/${String(deletePaymentMethod?.expDate?.year).substring(2, 4)} **** ${deletePaymentMethod?.last4} card from history?`;

  if (deletePaymentMethod) {
    return (
      <CustomConfirmationModal
        description={deletePaymentMethodDescription}
        buttonText="Delete"
        isSubmitting={isLoading}
        onCancel={() => setDeletePaymentMethod(undefined)}
        onSuccess={() => handleCancelPaymentMethod(deletePaymentMethod)}
      />
    );
  }

  return (
    <ModalWrapper width="600px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Payment Method History</ModalTitle>
      <Content>
        <div className="tableWrapper">
          <Table>
            <Thead $white>
              <Tr>
                <Th className="!text-center">TYPE</Th>
                <Th>CREATED</Th>
                <Th>LAST 4 DIGITS</Th>
                <Th>DEFAULT</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {paymentMethods.map((data, index) => (
                <Tr key={data.id || index} className={data.className}>
                  <Td>
                    <p className="active">{data.type}</p>
                  </Td>
                  <Td>
                    <p>{getAmericanTime(Number(data.created) * 1000)}</p>
                  </Td>
                  <Td>
                    <p>**** {data.last4}</p>
                  </Td>
                  <Td>
                    <div className="centerText">
                      <CheckBox
                        name={`default-method-${data.id}`}
                        rounded={true}
                        onChange={() => handleSetDefaultPaymentMethod(data.id)}
                        checked={data.default}
                        disabled={false} // TODO: change logic here with new permissions on Organizations for Billing Accounts
                      />
                    </div>
                  </Td>
                  <Td>
                    <IconButtonDelete
                      onClick={() => {
                        setDeletePaymentMethod(data);
                      }}
                      disabled={false} // TODO: change logic here with new permissions on Organizations for Billing Accounts
                    >
                      <TrashBin />
                    </IconButtonDelete>
                  </Td>
                </Tr>
              ))}
              {!isLoading && hasLoadError ? (
                <Tr className="no-border">
                  <Td colSpan={5} className="!max-w-none">
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-sm">Unable to load payment methods.</span>
                      <TextButton className="!px-0" onClick={() => void load()}>
                        Try again
                      </TextButton>
                    </div>
                  </Td>
                </Tr>
              ) : null}
              {!isLoading && !hasLoadError && paymentMethods.length === 0 ? (
                <Tr className="no-border">
                  <Td colSpan={5} className="!max-w-none text-center text-sm text-foreground-muted">
                    No payment methods yet.
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </div>
        <ModalFooterEnd>
          <OutlinedButton onClick={() => onCancel()}>Cancel</OutlinedButton>
        </ModalFooterEnd>
      </Content>
    </ModalWrapper>
  );
};
